import qs from "qs";

import browserHistory from "../history";

const STORAGE_EVENT_KEY = "ambifi:launch-tab:event";
const SESSION_TAB_ID_KEY = "ambifi:launch-tab:id";
const SESSION_TAB_NAME_KEY = "ambifi:launch-tab:name";
const CHANNEL_NAME = "ambifi-launch-tab-routing-v1";
const MESSAGE_TYPE_REQUEST = "AMBIFI_LAUNCH_TAB_REQUEST";
const MESSAGE_TYPE_ACK = "AMBIFI_LAUNCH_TAB_ACK";
const DEFAULT_TAB_NAME = "ambifiDeepLink";
const HANDOFF_TIMEOUT_MS = 650;
const TAB_OWNER_STORAGE_PREFIX = "ambifi:launch-tab:owner:";
const TAB_OWNER_HEARTBEAT_MS = 4000;
const TAB_OWNER_STALE_MS = 15000;
const AUTO_CLAIM_HOSTNAMES = new Set([
	"app.ambifi.com",
	"hca.ambifi.com",
	"local.ambifi.com",
	"localhost"
]);
const QUERY_PARAM_TAB_NAME_KEYS = ["tabName", "ambifiTab", "launchTab"];
const QUERY_PARAM_HANDOFF_KEYS = ["handoffTo", "tabTarget", "reuseTab"];

let channel = null;
let receiverInitialized = false;
let seenMessageIds = {};
let ownerHeartbeatHandle = null;
let ownerUnloadRegistered = false;

function normalizeText(value) {
	return String(value || "").trim();
}

function getTabId() {
	let tabId;

	try {
		tabId = normalizeText(window.sessionStorage.getItem(SESSION_TAB_ID_KEY));
		if (tabId === "") {
			tabId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
			window.sessionStorage.setItem(SESSION_TAB_ID_KEY, tabId);
		}
	} catch (_err) {
		tabId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
	}

	return tabId;
}

function getParsedQuery(search = window.location.search) {
	return qs.parse(search, { ignoreQueryPrefix: true });
}

function getCurrentHostname(location = window.location) {
	return normalizeText(location && location.hostname).toLowerCase();
}

function getFirstQueryValue(params, keys) {
	let index;
	let value;

	for (index = 0; index < keys.length; index += 1) {
		value = normalizeText(params[keys[index]]);
		if (value !== "") {
			return value;
		}
	}

	return "";
}

function getCleanSearch(params, keysToRemove = [...QUERY_PARAM_TAB_NAME_KEYS, ...QUERY_PARAM_HANDOFF_KEYS]) {
	const nextParams = { ...params };
	keysToRemove.forEach((key) => {
		delete nextParams[key];
	});

	const query = qs.stringify(nextParams, { addQueryPrefix: true, skipNulls: true });
	return query === "?" ? "" : query;
}

function getMessageId() {
	return `${getTabId()}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function getOwnerStorageKey(tabName) {
	return `${TAB_OWNER_STORAGE_PREFIX}${normalizeText(tabName)}`;
}

function readTabOwner(tabName) {
	let rawValue = "";

	if (normalizeText(tabName) === "") {
		return null;
	}

	try {
		rawValue = normalizeText(window.localStorage.getItem(getOwnerStorageKey(tabName)));
	} catch (_err) {
		rawValue = "";
	}

	if (rawValue === "") {
		return null;
	}

	try {
		return JSON.parse(rawValue);
	} catch (_err) {
		return null;
	}
}

function writeTabOwner(tabName) {
	if (normalizeText(tabName) === "") {
		return;
	}

	try {
		window.localStorage.setItem(getOwnerStorageKey(tabName), JSON.stringify({
			tabId: getTabId(),
			timestamp: Date.now()
		}));
	} catch (_err) {
		// Ignore ownership persistence failures.
	}
}

function clearTabOwner(tabName) {
	const normalizedTabName = normalizeText(tabName);
	const owner = readTabOwner(normalizedTabName);

	if (normalizedTabName === "") {
		return;
	}

	if (owner && normalizeText(owner.tabId) !== getTabId()) {
		return;
	}

	try {
		window.localStorage.removeItem(getOwnerStorageKey(normalizedTabName));
	} catch (_err) {
		// Ignore ownership cleanup failures.
	}
}

function isTabOwnerActive(owner) {
	const ownerTimestamp = Number(owner && owner.timestamp);

	if (!owner || normalizeText(owner.tabId) === "" || !Number.isFinite(ownerTimestamp)) {
		return false;
	}

	return (Date.now() - ownerTimestamp) < TAB_OWNER_STALE_MS;
}

function startTabOwnerHeartbeat(tabName) {
	const normalizedTabName = normalizeText(tabName);

	if (normalizedTabName === "") {
		return;
	}

	if (ownerHeartbeatHandle) {
		window.clearInterval(ownerHeartbeatHandle);
		ownerHeartbeatHandle = null;
	}

	writeTabOwner(normalizedTabName);

	ownerHeartbeatHandle = window.setInterval(() => {
		writeTabOwner(normalizedTabName);
	}, TAB_OWNER_HEARTBEAT_MS);

	if (!ownerUnloadRegistered) {
		ownerUnloadRegistered = true;
		window.addEventListener("beforeunload", () => {
			const activeTabName = normalizeText(window.name) || normalizeText(window.sessionStorage.getItem(SESSION_TAB_NAME_KEY));
			clearTabOwner(activeTabName);
		});
	}
}

function maybeAutoClaimDefaultTabName(location = window.location) {
	const hostname = getCurrentHostname(location);
	const owner = readTabOwner(DEFAULT_TAB_NAME);

	if (!AUTO_CLAIM_HOSTNAMES.has(hostname)) {
		return "";
	}

	if (owner && isTabOwnerActive(owner) && normalizeText(owner.tabId) !== getTabId()) {
		return "";
	}

	startTabOwnerHeartbeat(DEFAULT_TAB_NAME);
	return DEFAULT_TAB_NAME;
}

function markMessageSeen(messageId) {
	if (!messageId) {
		return;
	}

	seenMessageIds[messageId] = Date.now();

	Object.keys(seenMessageIds).forEach((key) => {
		if ((Date.now() - seenMessageIds[key]) > 30000) {
			delete seenMessageIds[key];
		}
	});
}

function hasSeenMessage(messageId) {
	return !!(messageId && seenMessageIds[messageId]);
}

function getChannel() {
	if (channel || typeof window === "undefined" || typeof window.BroadcastChannel !== "function") {
		return channel;
	}

	channel = new window.BroadcastChannel(CHANNEL_NAME);
	return channel;
}

function emitMessage(payload) {
	const message = {
		...payload,
		messageId: payload.messageId || getMessageId(),
		sourceTabId: payload.sourceTabId || getTabId(),
		timestamp: Date.now()
	};

	try {
		const bc = getChannel();
		if (bc) {
			bc.postMessage(message);
		}
	} catch (_err) {
		// Ignore BroadcastChannel failures and use storage fallback below.
	}

	try {
		window.localStorage.setItem(STORAGE_EVENT_KEY, JSON.stringify(message));
		window.localStorage.removeItem(STORAGE_EVENT_KEY);
	} catch (_err) {
		// Ignore storage failures.
	}

	return message;
}

function subscribeToMessages(handler) {
	const bc = getChannel();
	const bcListener = (event) => {
		if (!event || !event.data) {
			return;
		}

		if (hasSeenMessage(event.data.messageId)) {
			return;
		}

		markMessageSeen(event.data.messageId);
		handler(event.data);
	};
	const storageListener = (event) => {
		let payload;

		if (!event || event.key !== STORAGE_EVENT_KEY || !event.newValue) {
			return;
		}

		try {
			payload = JSON.parse(event.newValue);
		} catch (_err) {
			return;
		}

		if (hasSeenMessage(payload.messageId)) {
			return;
		}

		markMessageSeen(payload.messageId);
		handler(payload);
	};

	if (bc) {
		bc.addEventListener("message", bcListener);
	}
	window.addEventListener("storage", storageListener);

	return () => {
		if (bc) {
			bc.removeEventListener("message", bcListener);
		}
		window.removeEventListener("storage", storageListener);
	};
}

export function getCleanLaunchPath(location = window.location) {
	const params = getParsedQuery(location.search);
	const pathname = normalizeText(location.pathname);
	const hash = normalizeText(location.hash);

	return `${pathname}${getCleanSearch(params, [...QUERY_PARAM_TAB_NAME_KEYS, ...QUERY_PARAM_HANDOFF_KEYS])}${hash}`;
}

export function getLaunchPathWithoutTabName(location = window.location) {
	const params = getParsedQuery(location.search);
	const pathname = normalizeText(location.pathname);
	const hash = normalizeText(location.hash);

	return `${pathname}${getCleanSearch(params, [...QUERY_PARAM_TAB_NAME_KEYS])}${hash}`;
}

export function getRequestedLaunchTabName(location = window.location) {
	const params = getParsedQuery(location.search);
	return getFirstQueryValue(params, QUERY_PARAM_TAB_NAME_KEYS);
}

export function getRequestedHandoffTarget(location = window.location) {
	const params = getParsedQuery(location.search);
	return getFirstQueryValue(params, QUERY_PARAM_HANDOFF_KEYS);
}

export function getActiveLaunchTabName() {
	const explicitTabName = getRequestedLaunchTabName();
	const windowName = normalizeText(typeof window !== "undefined" ? window.name : "");
	let stickyTabName = "";

	try {
		stickyTabName = normalizeText(window.sessionStorage.getItem(SESSION_TAB_NAME_KEY));
	} catch (_err) {
		stickyTabName = "";
	}

	return explicitTabName || windowName || stickyTabName || maybeAutoClaimDefaultTabName();
}

export function initializeLaunchTabContext() {
	const activeTabName = getActiveLaunchTabName();
	const pathWithoutTabName = getLaunchPathWithoutTabName();

	if (activeTabName !== "") {
		try {
			window.name = activeTabName;
		} catch (_err) {
			// Ignore window.name errors.
		}

		try {
			window.sessionStorage.setItem(SESSION_TAB_NAME_KEY, activeTabName);
		} catch (_err) {
			// Ignore session storage errors.
		}

		if (activeTabName === DEFAULT_TAB_NAME) {
			startTabOwnerHeartbeat(activeTabName);
		}
	}

	if (pathWithoutTabName !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
		try {
			window.history.replaceState(window.history.state, document.title, pathWithoutTabName);
		} catch (_err) {
			// Ignore URL cleanup failures.
		}
	}

	return {
		tabName: activeTabName,
		defaultTabName: DEFAULT_TAB_NAME,
		cleanLaunchPath: getCleanLaunchPath()
	};
}

function defaultNavigateToLaunchPath(launchPath) {
	const currentPath = `${normalizeText(window.location.pathname)}${normalizeText(window.location.search)}${normalizeText(window.location.hash)}`;

	if (currentPath === launchPath) {
		return;
	}

	try {
		window.focus();
	} catch (_err) {
		// Ignore focus failures.
	}

	browserHistory.push(launchPath);
}

export function initializeLaunchTabReceiver(options = {}) {
	const tabName = options.tabName || getActiveLaunchTabName();
	const navigateToLaunchPath = options.navigateToLaunchPath || defaultNavigateToLaunchPath;

	if (receiverInitialized || tabName === "") {
		return tabName;
	}

	receiverInitialized = true;

	subscribeToMessages((message) => {
		if (!message || message.type !== MESSAGE_TYPE_REQUEST) {
			return;
		}

		if (normalizeText(message.targetTabName) !== tabName) {
			return;
		}

		if (normalizeText(message.sourceTabId) === getTabId()) {
			return;
		}

		emitMessage({
			type: MESSAGE_TYPE_ACK,
			requestId: message.requestId,
			targetTabName: tabName
		});

		navigateToLaunchPath(normalizeText(message.launchPath) || "/home");
	});

	return tabName;
}

export function attemptLaunchTabHandoff(options = {}) {
	const targetTabName = options.targetTabName || getRequestedHandoffTarget();
	const cleanLaunchPath = options.launchPath || getCleanLaunchPath();
	const timeoutMs = options.timeoutMs || HANDOFF_TIMEOUT_MS;

	if (targetTabName === "") {
		return Promise.resolve({ handedOff: false, targetTabName: "", launchPath: cleanLaunchPath });
	}

	if (getActiveLaunchTabName() === targetTabName) {
		return Promise.resolve({ handedOff: false, targetTabName, launchPath: cleanLaunchPath, sameTab: true });
	}

	return new Promise((resolve) => {
		const requestId = getMessageId();
		let settled = false;
		const unsubscribe = subscribeToMessages((message) => {
			if (settled || !message || message.type !== MESSAGE_TYPE_ACK) {
				return;
			}

			if (normalizeText(message.requestId) !== requestId || normalizeText(message.targetTabName) !== targetTabName) {
				return;
			}

			settled = true;
			unsubscribe();
			resolve({ handedOff: true, targetTabName, launchPath: cleanLaunchPath });
		});

		emitMessage({
			type: MESSAGE_TYPE_REQUEST,
			requestId,
			targetTabName,
			launchPath: cleanLaunchPath
		});

		window.setTimeout(() => {
			if (settled) {
				return;
			}

			settled = true;
			unsubscribe();
			resolve({ handedOff: false, targetTabName, launchPath: cleanLaunchPath });
		}, timeoutMs);
	});
}

export function renderLaunchTabHandoffScreen(targetTabName = DEFAULT_TAB_NAME) {
	const root = document.getElementById("root");

	if (!root) {
		return;
	}

	root.innerHTML = `
		<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#0f1725;color:#ffffff;padding:32px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;">
			<div style="max-width:560px;text-align:center;">
				<div style="font-size:22px;font-weight:700;margin-bottom:12px;">Opening in your existing AmbiFi tab...</div>
				<div style="font-size:16px;line-height:1.5;opacity:0.88;">
					We found your reusable AmbiFi tab <strong>${targetTabName}</strong> and handed the deep link off there.
					This tab can be closed.
				</div>
			</div>
		</div>
	`;

	window.setTimeout(() => {
		try {
			window.close();
		} catch (_err) {
			// Ignore close failures.
		}
	}, 250);
}

export function getDefaultLaunchTabName() {
	return DEFAULT_TAB_NAME;
}
